import { ApolloQueryResult } from "@apollo/client";
import { Col, Form, Input, Modal, Row, Select, Switch } from "antd";
import useListCurrencies from "hooks/useListCurrencies";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { getFormError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import useBankApi from "./hooks/useBankApi";
import React from "react";
import { FaPlus } from "react-icons/fa6";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import { useParams } from "react-router-dom";

interface CreateUpdateBankPropsTypes {
  refetchCompanyBanks: (
    variables?: Partial<ListCompaniesBankAccountsPaginatedInput> | undefined,
  ) => Promise<ApolloQueryResult<ListCompaniesBankAccountsPaginatedOutput>>;
  id?: ID;
  bankId?: ID;
}

export default function CreateUpdateBankAccount(
  props: CreateUpdateBankPropsTypes,
): React.ReactElement {
  const { id, bankId, refetchCompanyBanks } = props;
  const urlParams: RouteParams = useParams();

  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [isOpen, setIsOpen] = useState(false);
  const [bankAccountId, setBankAccountId] = useState<ID>();
  const {
    createUpdateCompanyBankAccount,
    getCompanyBankAccount,
    bankAccountData: data,
  } = useBankApi(bankAccountId);

  const { notificationError, notificationSuccessfullySaved } = useNotification();

  const { listCurrencies, currenciesOptions } = useListCurrencies();

  const integration = Form.useWatch("integration", form);
  const isPowens = integration === "powens";

  const onFinish = async (values: Record<string, any>) => {
    delete values.isActive;
    const updateId = bankAccountId
      ? { id: bankAccountId }
      : { companyId: urlParams.companyId, bank: bankId };

    let payload = values;
    if (bankAccountId && data) {
      const original: Record<string, unknown> = { ...data, bank: data?.bank?.id };
      payload = Object.fromEntries(
        Object.entries(values).filter(([key, value]) => value !== original[key]),
      );
    }

    const variables = {
      input: {
        ...payload,
        ...updateId,
      },
    };

    const response = await createUpdateCompanyBankAccount({
      variables: variables as unknown as NonNullable<
        Parameters<typeof createUpdateCompanyBankAccount>[0]
      >["variables"],
    });

    const responseData = response?.data as Record<string, { errors?: any[] }> | undefined;
    const errors = bankAccountId
      ? responseData?.updateCompanyBankAccount?.errors
      : responseData?.createCompanyBankAccount?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      form.setFields(fieldsData);
      notificationError(errors);
    } else {
      refetchCompanyBanks();
      notificationSuccessfullySaved(t("Payment"));
      setIsOpen(false);
      setBankAccountId(undefined);
      form.resetFields();
    }
  };

  useEffect(() => {
    if (bankAccountId) {
      getCompanyBankAccount({
        variables: {
          id: bankAccountId,
        },
      });
    }
  }, [bankAccountId]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, bank: data?.bank?.id });
    }
  }, [data]);

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsOpen(false);
    setBankAccountId(undefined);
    form.resetFields();
  };

  const handleOnClickEditBank = () => {
    setIsOpen(true);
    setBankAccountId(id);
    listCurrencies();
  };
  const handleOnClickAddBank = () => {
    setIsOpen(true);
    listCurrencies();
  };

  return (
    <>
      <Row gutter={16} justify="space-evenly">
        {id && (
          <Col span={4}>
            <TooltipButton
              title={t("Edit bank")}
              placement="top"
              disabled={!bankId}
              handleOnClick={handleOnClickEditBank}
              icon={<MdEdit />}
            />
          </Col>
        )}
        <Col span={4}>
          <TooltipButton
            title={t("Add bank")}
            placement="top"
            disabled={!bankId}
            handleOnClick={handleOnClickAddBank}
            icon={<FaPlus />}
          />
        </Col>
      </Row>
      <Modal
        title={t("Bank accounts")}
        open={isOpen}
        onOk={handleOk}
        okText={t("Save")}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        onCancel={handleCancel}
        width={"50%"}
        className="antd-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{ isMain: false }}
        >
          <Row gutter={8}>
            <Form.Item name="integration" hidden>
              <Input />
            </Form.Item>
            <Col lg={12} sm={24} xs={24}>
              <Form.Item
                label="IBAN"
                name="iban"
                rules={[{ required: !isPowens, message: t("This field is required") }]}
              >
                <Input disabled={isPowens} />
              </Form.Item>
            </Col>
            <Col lg={4} sm={8} xs={24}>
              <Form.Item
                label="BIC/SWIFT"
                name="bic"
                rules={[{ required: !isPowens, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col lg={6} sm={8} xs={24}>
              <Form.Item
                label={t("Currency")}
                name="currency"
                rules={[{ required: !isPowens, message: t("This field is required") }]}
              >
                <Select
                  disabled={isPowens}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().startsWith(input.toLowerCase());
                  }}
                  filterSort={(optionA, optionB) => {
                    const valueA = String(optionA?.value ?? "");
                    const valueB = String(optionB?.value ?? "");

                    if (valueA === "EUR") return -1;
                    if (valueB === "EUR") return 1;

                    const labelA = String(optionA?.label ?? "");
                    const labelB = String(optionB?.label ?? "");
                    return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
                  }}
                  options={currenciesOptions}
                />
              </Form.Item>
            </Col>
            <Col lg={8} sm={16} xs={24}>
              <Form.Item
                label={t("Name")}
                name="name"
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col lg={12} sm={16} xs={24}>
              <Form.Item label={t("Bank description")} name="bankDescription">
                <Input />
              </Form.Item>
            </Col>
            <Col lg={12} sm={12} xs={24}>
              <Form.Item label={t("SEPA Credential Identifier")} name="sepaCredentialIdentifier">
                <Input />
              </Form.Item>
            </Col>
            <Col lg={4} sm={4} xs={24}>
              <Form.Item label={t("Main Bank")} name="isMain" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col lg={4} sm={3} xs={24}>
              <Form.Item
                label={t("Active")}
                name="isActive"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
